import React from 'react';
import '@aws-amplify/ui-react/styles.css';

import './App.css';
import AppRouter from './AppRouter';
import { AppLocalConfig } from './AppLocalConfig';
import { AuthContextProvider } from './store/AuthContext';
import AuthContainer from './components/UI/AuthContainer';
import useAuth from './util/useAuth';
import { TenantConfigurationProvider } from './store/TenantConfigurationProvider';

/**
 * Extract tenant ID from the hostname
 * Examples:
 * - sparkmeter.localhost:3000 -> sparkmeter
 * - sparkmeter.dev.gridscan.sparkmeter.io -> sparkmeter
 * - localhost:3000 -> sparkmeter (default for local testing)
 */
const getTenantId = () => {
  const hostname = window.location.hostname;
  
  // For localhost development, always use sparkmeter tenant
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return 'sparkmeter';
  }
  
  // Extract the first part of the hostname (everything before the first dot)
  const [tenantId] = hostname.split('.');
  
  if (!tenantId) {
    console.error('Could not extract tenant ID from hostname:', hostname);
    return null;
  }
  
  return tenantId;
};

/**
 * Main App component
 * Handles tenant configuration, authentication flow and renders appropriate UI
 */
const App = () => {
  const tenantId = getTenantId();
  
  // If no tenant ID is available, show error with instructions
  if (!tenantId) {
    return (
      <div className="error-container">
        <h1>Configuration Error</h1>
        <p>No tenant ID found in the hostname.</p>
        <p>Please access the application using one of these formats:</p>
        <ul>
          <li>Local development: <code>{'<tenant>'}.localhost:3000</code></li>
          <li>Development environment: <code>{'<tenant>'}.dev.gridscan.sparkmeter.io</code></li>
        </ul>
      </div>
    );
  }

  return (
    <TenantConfigurationProvider tenantId={tenantId}>
      <AppContent />
    </TenantConfigurationProvider>
  );
};

/**
 * AppContent component
 * Rendered after tenant configuration is loaded
 * Handles authentication state and routing
 */
const AppContent = () => {
  const { isAuthenticated, isInitializing, authError, signIn } = useAuth();

  if (!isAuthenticated) {
    return (
      <AuthContainer 
        isLoading={isInitializing}
        onSignIn={signIn}
        error={authError}
      />
    );
  }

  return (
    <AuthContextProvider>
      <AppLocalConfig>
        <AppRouter />
      </AppLocalConfig>
    </AuthContextProvider>
  );
};

export default App;
